/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace bernoulli
*/
var bernoulli = {};

/**
* @name cdf
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/cdf}
*/
import cdf from './../../../../base/dists/bernoulli/cdf';
setReadOnly( bernoulli, 'cdf', cdf );

/**
* @name Bernoulli
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/ctor}
*/
import Bernoulli from './../../../../base/dists/bernoulli/ctor';
setReadOnly( bernoulli, 'Bernoulli', Bernoulli );

/**
* @name entropy
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/entropy}
*/
import entropy from './../../../../base/dists/bernoulli/entropy';
setReadOnly( bernoulli, 'entropy', entropy );

/**
* @name kurtosis
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/kurtosis}
*/
import kurtosis from './../../../../base/dists/bernoulli/kurtosis';
setReadOnly( bernoulli, 'kurtosis', kurtosis );

/**
* @name mean
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/mean}
*/
import mean from './../../../../base/dists/bernoulli/mean';
setReadOnly( bernoulli, 'mean', mean );

/**
* @name median
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/median}
*/
import median from './../../../../base/dists/bernoulli/median';
setReadOnly( bernoulli, 'median', median );

/**
* @name mgf
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/mgf}
*/
import mgf from './../../../../base/dists/bernoulli/mgf';
setReadOnly( bernoulli, 'mgf', mgf );

/**
* @name mode
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/mode}
*/
import mode from './../../../../base/dists/bernoulli/mode';
setReadOnly( bernoulli, 'mode', mode );

/**
* @name pmf
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/pmf}
*/
import pmf from './../../../../base/dists/bernoulli/pmf';
setReadOnly( bernoulli, 'pmf', pmf );

/**
* @name quantile
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/quantile}
*/
import quantile from './../../../../base/dists/bernoulli/quantile';
setReadOnly( bernoulli, 'quantile', quantile );

/**
* @name skewness
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/skewness}
*/
import skewness from './../../../../base/dists/bernoulli/skewness';
setReadOnly( bernoulli, 'skewness', skewness );

/**
* @name stdev
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/stdev}
*/
import stdev from './../../../../base/dists/bernoulli/stdev';
setReadOnly( bernoulli, 'stdev', stdev );

/**
* @name variance
* @memberof bernoulli
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/bernoulli/variance}
*/
import variance from './../../../../base/dists/bernoulli/variance';
setReadOnly( bernoulli, 'variance', variance );


// EXPORTS //

export default bernoulli;
